import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Compass } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { MaskedLines, EASE } from "@/components/Reveal";

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

    return (
        <section ref={ref} className="relative flex h-[100svh] min-h-[640px] items-end overflow-hidden bg-navy" data-testid="hero-section">
            <motion.img
                style={{ y, scale }}
                src={IMAGES.hero}
                alt="Golden hour over Himalayan peaks in India"
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/70 to-navy/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-navy/40" />

            <motion.div
                initial={{ opacity: 0, rotate: -20 }}
                animate={{ opacity: 0.35, rotate: 0 }}
                transition={{ duration: 1.6, delay: 0.9, ease: EASE }}
                className="pointer-events-none absolute right-[8%] top-[18%] hidden text-brass lg:block"
            >
                <Compass className="h-28 w-28 animate-slow-spin" strokeWidth={0.75} />
            </motion.div>

            <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                    className="eyebrow mb-6"
                >
                    Vistaaryatra Pvt. Ltd. · Tours &amp; Travels
                </motion.p>
                <MaskedLines
                    lines={["Unfold Extraordinary", "Journeys Across India"]}
                    lineClassName="font-serif text-4xl font-semibold tracking-tight text-ivory sm:text-6xl lg:text-7xl leading-[1.08]"
                    delay={0.35}
                />
                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg"
                >
                    Bespoke domestic tours — royal palaces, Himalayan escapes, backwater cruises and golden
                    coasts — designed end-to-end by travel specialists who care about the details.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <Link
                        to="/book"
                        data-testid="hero-book-now-button"
                        className="group inline-flex items-center gap-2 rounded-full bg-brass px-8 py-4 text-sm font-semibold text-navy transition-all duration-300 hover:bg-brass-dark hover:shadow-[0_15px_40px_-10px_rgba(212,175,55,0.6)]"
                    >
                        Book Your Journey
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                    <Link
                        to="/packages"
                        data-testid="hero-packages-button"
                        className="inline-flex items-center gap-2 rounded-full border border-ivory/40 px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:border-brass hover:text-brass"
                    >
                        Explore Packages
                    </Link>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 right-28 hidden items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-ivory/50 lg:flex"
            >
                <span className="h-px w-10 bg-brass/60" />
                Domestic Tour Specialists
            </motion.div>
        </section>
    );
};
